import { useMemo, useState } from 'react'
import { Button } from '@/ui/components'
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/components'
import { Input } from '@/ui/components'
import { Label } from '@/ui/components'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/ui/components'
import type { VehicleApi } from '@/shared/api/vehicles'
import { useVehicleOnboarding } from '../hooks'

interface OnboardingWizardProps {
  onComplete: (vehicleProfile: any, gearItems: any[]) => void
  onSkip: () => void
  vehicleApi: VehicleApi
}

export function OnboardingWizard({ onComplete, onSkip, vehicleApi }: OnboardingWizardProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    makeOptions,
    modelOptions,
    selectedMake,
    selectedModel,
    year,
    isLoadingMakes,
    isLoadingModels,
    error,
    setMake,
    setModel,
    setYear,
    canSubmit,
    clearError
  } = useVehicleOnboarding({ vehicleApi })

  const spotlightMakes = useMemo(() => {
    const preferredMakes = ['Toyota', 'Jeep', 'Ford', 'Subaru', 'Chevrolet']
    return preferredMakes
      .map(preferred =>
        makeOptions.find(option => option.name.toLowerCase() === preferred.toLowerCase())
      )
      .filter((value): value is NonNullable<typeof value> => Boolean(value))
  }, [makeOptions])

  function handleMakeChange(value: string) {
    clearError()
    setMake(value)
  }

  function handleModelChange(value: string) {
    clearError()
    setModel(value)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setIsSubmitting(true)
    try {
      const vehicleProfile = {
        make: selectedMake,
        model: selectedModel,
        year: Number(year)
      }
      const gearItems: any[] = []
      await onComplete(vehicleProfile, gearItems)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-adventure-50/60 py-12 dark:bg-earth-900/60">
      <div className="mx-auto max-w-xl px-4 sm:px-0">
        <Card className="border border-primary/10 bg-background shadow-lg shadow-primary/5">
          <CardHeader className="space-y-2">
            <CardTitle className="text-2xl font-semibold text-foreground">
              Set up your adventure rig
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              We&apos;ll tailor your maps, checklists, and upgrade suggestions based on the vehicle you
              rely on. Choose from curated makes and models to bypass typos and mismatches.
            </p>
          </CardHeader>
          <CardContent>
            <form className="space-y-5" onSubmit={handleSubmit}>
              {spotlightMakes.length > 0 ? (
                <div className="space-y-2 rounded-lg border border-primary/20 bg-primary/5 p-3 dark:border-primary/30 dark:bg-primary/10">
                  <p className="text-xs font-medium uppercase tracking-wide text-primary dark:text-primary">
                    Popular overlanding brands
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {spotlightMakes.map(make => (
                      <Button
                        key={make.id}
                        type="button"
                        variant={selectedMake === make.name ? 'default' : 'outline'}
                        className={
                          selectedMake === make.name
                            ? 'bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-primary'
                            : 'border-primary/30 text-primary hover:bg-primary/10 hover:border-primary/50 focus-visible:ring-primary'
                        }
                        onClick={() => handleMakeChange(make.name)}
                        disabled={isLoadingMakes}
                      >
                        {make.name}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="space-y-2">
                <Label htmlFor="vehicle-make">Make</Label>
                <Select value={selectedMake} onValueChange={handleMakeChange} disabled={isLoadingMakes}>
                  <SelectTrigger id="vehicle-make">
                    <SelectValue placeholder={isLoadingMakes ? 'Loading makes…' : 'Select a make'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {makeOptions.map(make => (
                        <SelectItem key={make.id} value={make.name}>
                          {make.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Start typing the make name to jump through the list quickly.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vehicle-model">Model</Label>
                <Select
                  value={selectedModel}
                  onValueChange={handleModelChange}
                  disabled={!selectedMake || isLoadingModels}
                >
                  <SelectTrigger id="vehicle-model">
                    <SelectValue
                      placeholder={
                        !selectedMake
                          ? 'Choose a make first'
                          : isLoadingModels
                            ? 'Loading models…'
                            : 'Select a model'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {modelOptions.map(model => (
                        <SelectItem key={model.id} value={model.name}>
                          {model.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Models are filtered automatically when you pick a make. We surface the latest model
                  year first.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vehicle-year">Year</Label>
                <Input
                  id="vehicle-year"
                  value={year}
                  onChange={e => {
                    clearError()
                    setYear(e.target.value)
                  }}
                  inputMode="numeric"
                  placeholder="2020"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Enter a four-digit year between 1970 and next year. Use your build year if the rig is
                  heavily modified.
                </p>
              </div>
              {error ? (
                <p className="text-sm text-destructive">
                  {error}
                </p>
              ) : null}
              <div className="flex flex-col gap-2 pt-2 sm:flex-row">
                <Button
                  type="submit"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-primary sm:w-auto"
                  disabled={!canSubmit || isSubmitting}
                >
                  {isSubmitting ? 'Saving…' : 'Save Vehicle'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-primary/30 text-primary hover:bg-primary/10 hover:border-primary/50 focus-visible:ring-primary sm:w-auto"
                  onClick={onSkip}
                >
                  Skip
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
